/**
 * Feriados nacionais (fixos + móveis via Páscoa) e estaduais (ES, SC, RS, SP, PR).
 */

export type FeriadoTipo = 'nacional' | 'estadual';
export interface Feriado {
  date: string; // YYYY-MM-DD
  nome: string;
  tipo: FeriadoTipo;
  uf?: 'ES' | 'SC' | 'RS' | 'SP' | 'PR';
}

// Algoritmo de Gauss pra calcular a Páscoa de qualquer ano
function paschal(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function iso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export function feriadosDoAno(year: number): Feriado[] {
  const pasc = paschal(year);
  const carnavalSeg = addDays(pasc, -48);
  const carnavalTer = addDays(pasc, -47);
  const sextaSanta  = addDays(pasc, -2);
  const corpus      = addDays(pasc, 60);

  const nacionais: Feriado[] = [
    { date: `${year}-01-01`, nome: 'Confraternização Universal', tipo: 'nacional' },
    { date: iso(carnavalSeg), nome: 'Carnaval (segunda)',        tipo: 'nacional' },
    { date: iso(carnavalTer), nome: 'Carnaval (terça)',          tipo: 'nacional' },
    { date: iso(sextaSanta),  nome: 'Sexta-feira Santa',         tipo: 'nacional' },
    { date: iso(pasc),        nome: 'Páscoa',                    tipo: 'nacional' },
    { date: `${year}-04-21`,  nome: 'Tiradentes',                tipo: 'nacional' },
    { date: `${year}-05-01`,  nome: 'Dia do Trabalho',           tipo: 'nacional' },
    { date: iso(corpus),      nome: 'Corpus Christi',            tipo: 'nacional' },
    { date: `${year}-09-07`,  nome: 'Independência',             tipo: 'nacional' },
    { date: `${year}-10-12`,  nome: 'N. Sra. Aparecida',         tipo: 'nacional' },
    { date: `${year}-11-02`,  nome: 'Finados',                   tipo: 'nacional' },
    { date: `${year}-11-15`,  nome: 'Proclamação da República',  tipo: 'nacional' },
    { date: `${year}-11-20`,  nome: 'Consciência Negra',         tipo: 'nacional' },
    { date: `${year}-12-25`,  nome: 'Natal',                     tipo: 'nacional' },
  ];

  const estaduais: Feriado[] = [
    { date: `${year}-07-09`, nome: 'Revolução Constitucionalista', tipo: 'estadual', uf: 'SP' },
    { date: `${year}-08-11`, nome: 'Criação do Estado',            tipo: 'estadual', uf: 'SC' },
    { date: `${year}-09-20`, nome: 'Revolução Farroupilha',        tipo: 'estadual', uf: 'RS' },
    { date: `${year}-09-08`, nome: 'NS da Penha (padroeira)',      tipo: 'estadual', uf: 'ES' },
    { date: `${year}-12-19`, nome: 'Emancipação Política',         tipo: 'estadual', uf: 'PR' },
  ];

  return [...nacionais, ...estaduais].sort((a, b) => a.date.localeCompare(b.date));
}

/** Mapa rápido: ISO-date → lista de feriados naquele dia */
export function buildFeriadoIndex(years: number[]): Map<string, Feriado[]> {
  const m = new Map<string, Feriado[]>();
  for (const y of years) {
    for (const f of feriadosDoAno(y)) {
      const list = m.get(f.date) ?? [];
      list.push(f);
      m.set(f.date, list);
    }
  }
  return m;
}
